import { IPersonaFront } from '@api/routers/user/service';
import { EndpointId } from '@layerzerolabs/lz-definitions';
import {
  Blockchain,
  PERSONA_CONTRACT_ABI,
  PERSONA_LZ_NFT_CONTRACT_ABI,
  STAKING_CONTRACT_ABI,
} from '@repo/prsn-blockchain';
import { getWalletClient } from '@wagmi/core';
import {
  Account,
  Address,
  Chain,
  Hex,
  PublicClient,
  SwitchChainError,
  Transport,
  WalletClient,
} from 'viem';

import { env } from '@web/utils/env';
import { chains, clients } from '@web/utils/rainbowkit/chains';
import { wagmiConfig } from '@web/utils/rainbowkit/WagmiProvider';

const endpoints =
  env.ENVIRONMENT === 'release'
    ? {
        ethereum: EndpointId.ETHEREUM_V2_MAINNET,
        base: EndpointId.BASE_V2_MAINNET,
      }
    : {
        ethereum: EndpointId.SEPOLIA_V2_TESTNET,
        base: EndpointId.BASESEP_V2_TESTNET,
      };

async function getWalletClientOrThrow() {
  const walletClient = await getWalletClient(wagmiConfig);
  if (!walletClient) {
    throw new Error('You must connect an EVM compatible wallet');
  }
  return walletClient;
}

async function switchChain(
  walletClient: Awaited<ReturnType<typeof getWalletClientOrThrow>>,
  chain: Chain,
  addChain = true,
) {
  try {
    await walletClient.switchChain?.(chain);
  } catch (e) {
    if ((e as SwitchChainError).code === 4902) {
      if (addChain && walletClient.addChain) {
        await walletClient.addChain({ chain });
        // Try again, but we'll not allow to add chain anymore.
        return switchChain(walletClient, chain, false);
      }

      throw new Error(`Please connect ${chain.name} to your wallet`);
    }
  }

  if ((await walletClient.getChainId()) !== chain.id) {
    throw new Error(`Please select ${chain.name} blockchain`);
  }
}

export async function prepareWallet(blockchain: Blockchain): Promise<{
  chain: Chain;
  publicClient: PublicClient;
  walletClient: WalletClient<Transport, Chain, Account>;
}> {
  const chain = chains[blockchain];
  const walletClient = await getWalletClientOrThrow();
  await switchChain(walletClient, chain);
  return {
    chain,
    walletClient,
    publicClient: clients[blockchain],
  };
}

export async function hasApprovalForAllNfts({
  blockchain,
  operator,
}: {
  blockchain: Blockchain;
  operator: Address;
}) {
  const walletClient = await getWalletClientOrThrow();
  return clients[blockchain].readContract({
    address: env[blockchain].PERSONA_ADDRESS,
    abi: PERSONA_CONTRACT_ABI,
    functionName: 'isApprovedForAll',
    args: [walletClient.account.address, operator],
  });
}

export async function setApprovalForAllNfts({
  blockchain,
  operator,
}: {
  blockchain: Blockchain;
  operator: Address;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(blockchain);
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env[blockchain].PERSONA_ADDRESS,
    abi: PERSONA_CONTRACT_ABI,
    functionName: 'setApprovalForAll',
    args: [operator, true],
  });
  return walletClient.writeContract(request);
}

export async function bridgeEthereumToBase({
  tokenIds,
  stake,
}: {
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
  stake?: boolean;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.ETHEREUM,
  );
  const quote = await publicClient.readContract({
    address: env.ethereum.BRIDGE_ADDRESS,
    abi: PERSONA_LZ_NFT_CONTRACT_ABI,
    functionName: 'quoteOriginalCollectionBridgeTransferFrom',
    args: [
      walletClient.account.address,
      walletClient.account.address,
      tokenIds,
      stake ?? false,
      endpoints.base,
    ],
  });
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.ethereum.BRIDGE_ADDRESS,
    abi: PERSONA_LZ_NFT_CONTRACT_ABI,
    functionName: 'originalCollectionBridgeTransferFrom',
    value: quote,
    args: [
      walletClient.account.address,
      walletClient.account.address,
      tokenIds,
      stake ?? false,
      endpoints.base,
    ],
  });
  return walletClient.writeContract(request);
}

async function bridgeBaseToEthereum({
  tokenIds,
}: {
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.BASE,
  );
  const quote = await publicClient.readContract({
    address: env.base.PERSONA_ADDRESS,
    abi: PERSONA_LZ_NFT_CONTRACT_ABI,
    functionName: 'quoteBridgeTransferFrom',
    args: [
      walletClient.account.address,
      walletClient.account.address,
      tokenIds,
      false,
      endpoints.ethereum,
    ],
  });
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.base.PERSONA_ADDRESS,
    abi: PERSONA_LZ_NFT_CONTRACT_ABI,
    functionName: 'bridgeTransferFrom',
    value: quote,
    args: [
      walletClient.account.address,
      walletClient.account.address,
      tokenIds,
      false,
      endpoints.ethereum,
    ],
  });
  return walletClient.writeContract(request);
}

export function bridge(params: {
  source: Blockchain;
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  if (params.source === Blockchain.ETHEREUM) {
    return bridgeEthereumToBase(params);
  } else {
    return bridgeBaseToEthereum(params);
  }
}

export async function stakeOnBase({
  tokenIds,
}: {
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.BASE,
  );
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.base.STAKING_ADDRESS,
    abi: STAKING_CONTRACT_ABI,
    functionName: 'deposit',
    args: [walletClient.account.address, tokenIds],
  });
  return walletClient.writeContract(request);
}

export async function claim({
  tokenIds,
}: {
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.BASE,
  );
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.base.STAKING_ADDRESS,
    abi: STAKING_CONTRACT_ABI,
    functionName: 'claim',
    args: [tokenIds],
  });
  return walletClient.writeContract(request);
}

export async function unstake({
  tokenIds,
}: {
  tokenIds: ReadonlyArray<IPersonaFront['tokenId']>;
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.BASE,
  );
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.base.STAKING_ADDRESS,
    abi: STAKING_CONTRACT_ABI,
    functionName: 'withdraw',
    args: [tokenIds],
  });
  return walletClient.writeContract(request);
}

interface IBoostSignature {
  expiration: number;
  nonce: bigint;
  value: Hex;
}

export async function boost({
  tokenIds,
  bonusesPerDay,
  signatures,
}: {
  tokenIds: Array<IPersonaFront['tokenId']>;
  bonusesPerDay: bigint[];
  signatures: IBoostSignature[];
}) {
  const { chain, walletClient, publicClient } = await prepareWallet(
    Blockchain.BASE,
  );
  const { request } = await publicClient.simulateContract({
    chain,
    account: walletClient.account,
    address: env.base.STAKING_ADDRESS,
    abi: STAKING_CONTRACT_ABI,
    functionName: 'setBonus',
    args: [tokenIds, bonusesPerDay, signatures as any],
  });
  return walletClient.writeContract(request);
}
